/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { IGdnPartSmartData } from './gdn-part-smart.data';
import { IGdnPartSmartProps } from './gdn-part-smart.props.autogenerated';
import MsDyn365, { RichTextComponent } from '@msdyn365-commerce/core';
import { getPartSmartPortalAccessAsync } from '../../actions/DataActionExtension.g';
import { IGetPartSmartTokenResponse } from '../../actions/DataServiceEntities.g';

export interface IGdnPartSmartViewProps extends IGdnPartSmartProps<IGdnPartSmartData> {
    containerRef: React.RefObject<HTMLDivElement>;
}

interface IGdnPartSmartState {
    partSmartTokens: IGetPartSmartTokenResponse | undefined;
    hasError: boolean;
}

/**
 *
 * GdnPartSmart component
 * @extends {React.PureComponent<IGdnPartSmartProps<IGdnPartSmartData>>}
 */
class GdnPartSmart extends React.PureComponent<IGdnPartSmartProps<IGdnPartSmartData>, IGdnPartSmartState> {
    private containerRef = React.createRef<HTMLDivElement>();

    public constructor(props: IGdnPartSmartProps<IGdnPartSmartData>) {
        super(props);
        this.state = { partSmartTokens: undefined, hasError: false };
    }

    public componentDidMount() {
        this.loadPortalTokens();
    }

    public componentDidUpdate(prevProps: IGdnPartSmartProps<IGdnPartSmartData>, prevState: IGdnPartSmartState) {
        if (!prevState.partSmartTokens && this.state.partSmartTokens) {
            this.appendPartSmartScript(this.state.partSmartTokens);
        }
    }

    public render(): JSX.Element | null {
        if (!MsDyn365.isBrowser) {
            return null;
        }

        const errorMessage = this.props.config.errorMessage && this.props.config.errorMessage;

        if (this.state.hasError) {
            return (
                <div className='partsmart-container__error'>
                    <RichTextComponent text={errorMessage} className='partsmart-container__error__message' />
                </div>
            );
        }

        const viewProps: IGdnPartSmartViewProps = {
            ...this.props,
            containerRef: this.containerRef
        };

        return this.props.renderView(viewProps);
    }

    private async loadPortalTokens() {
        try {
            const resp = await getPartSmartPortalAccessAsync({ callerContext: this.props.context.actionContext });
            this.checkErrorState(resp);
            this.setState({ partSmartTokens: resp });
        } catch (e) {
            this.setState({ hasError: true });
            console.error('Failed to load PartSmart tokens', e);
        }
    }

    private appendPartSmartScript(tokens: IGetPartSmartTokenResponse) {
        const { AccessToken, RefreshToken, ExpiresIn } = tokens;

        this.checkErrorState(tokens);

        if (this.state.hasError || !AccessToken || !RefreshToken || !ExpiresIn) {
            return;
        }

        const url = [
            'https://www.partsmart.net/stream.js',
            `?access_token=${encodeURIComponent(AccessToken)}`,
            `&refresh_token=${encodeURIComponent(RefreshToken)}`,
            `&expires_in=${encodeURIComponent(ExpiresIn)}`
        ].join('');

        const script = document.createElement('script');
        script.src = url;
        script.async = true;
        this.containerRef.current?.appendChild(script);
    }

    private checkErrorState(resp: IGetPartSmartTokenResponse) {
        const { AccessToken, RefreshToken, ExpiresIn } = resp;

        if (!AccessToken || !RefreshToken || !ExpiresIn) {
            this.setState({ hasError: true });
        }
    }
}

export default GdnPartSmart;
